import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { discardIssue, retryIssue } from '@/lib/sync';
import { dateTime, humanise } from '@/lib/format';
import { useSyncStore } from '@/stores/syncStore';
import { toast } from '@/stores/toastStore';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import { Drawer } from '@/components/ui/Drawer';

/**
 * Entries the outbox could not push and will not retry on its own: a
 * constraint the server rejected, a diary a supervisor has since approved,
 * a permission the role does not have.
 *
 * Conflicts are shown, never resolved silently. A supervisor deciding
 * between two versions of a shift is right; an algorithm guessing is not.
 */
function IssueList() {
  const parked = useLiveQuery(
    async () => db.outbox.filter((e) => e.next_attempt_at > '9000-01-01').toArray(),
    [],
  );

  if (!parked?.length) {
    return <p className="small muted" style={{ margin: 0 }}>Nothing is waiting. Every change on this device has reached the server.</p>;
  }

  async function retry(seq: number) {
    toast.info('Retrying…');
    await retryIssue(seq);
  }

  async function discard(seq: number): Promise<boolean> {
    const { restored } = await discardIssue(seq);
    toast.info(restored
      ? 'Change discarded — the saved version is back on this device'
      : 'Change discarded. Sync when online to bring back the saved version.');
    return true;
  }

  return (
    <>
      <p className="small muted" style={{ marginTop: 0 }}>
        The server refused these changes; the reason is under each one. Nothing
        has been lost. Correct what the reason asks and press <strong>Retry</strong>,
        or <strong>Discard</strong> to drop the change and go back to the version
        the server holds. A change your role is not allowed to make has to be
        made by someone who may.
      </p>

      <div className="issue-list">
        {parked.map((e) => (
          <div className="issue issue--block" key={e.seq} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div><strong>{humanise(e.entity)}</strong> · {e.op === 'delete' ? 'removal' : 'change'} · {dateTime(e.created_at)}</div>
              <div className="small">{e.last_error ?? 'Rejected by the server'}</div>
            </div>
            <Button variant="secondary" style={{ minHeight: 32 }} onClick={() => void retry(e.seq!)}>
              Retry
            </Button>
            <ConfirmRemove compact label="Discard" question="Discard this change?"
              onConfirm={() => discard(e.seq!)} />
          </div>
        ))}
      </div>
    </>
  );
}

/** The panel on the diary page. Hidden when nothing is waiting. */
export function SyncIssues() {
  const count = useSyncStore((s) => s.issues.length);
  if (!count) return null;
  return (
    <Panel title={`Sync issues · ${count}`}>
      <IssueList />
    </Panel>
  );
}

/** The same list on any screen, opened from the sync status chip. */
export function SyncIssuesDrawer() {
  const open = useSyncStore((s) => s.issuesOpen);
  const setOpen = useSyncStore((s) => s.setIssuesOpen);
  const count = useSyncStore((s) => s.issues.length);
  return (
    <Drawer open={open} width={600} title={`Sync issues · ${count}`}
      subtitle="Changes saved on this device that the server refused"
      onClose={() => setOpen(false)}
      footer={<><span className="spacer" /><Button variant="secondary" onClick={() => setOpen(false)}>Close</Button></>}>
      <IssueList />
    </Drawer>
  );
}
